import { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function Services() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", frequency: "annual", default_deadline_offset_days: 30,
    default_reminder_days_before: [30, 14, 7, 1], stages: ["Awaiting records", "In progress", "Client review", "Filed"],
  });

  const load = async () => {
    const r = await api.get("/service-templates");
    setRows(r.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/service-templates", form);
      toast.success("Template created");
      setOpen(false);
      setForm({ name: "", description: "", frequency: "annual", default_deadline_offset_days: 30, default_reminder_days_before: [30, 14, 7, 1], stages: ["Awaiting records", "In progress", "Client review", "Filed"] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await api.delete(`/service-templates/${id}`);
    load();
  };

  return (
    <div>
      <PageHeader
        testid="page-services"
        title="Service templates"
        subtitle={`${rows.length} template(s) available`}
        actions={
          isAdmin && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid="add-template-btn" className="rounded-md bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4 mr-2" /> New template</Button></DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>New service template</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                  <div><Label>Name *</Label><Input data-testid="template-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Frequency</Label>
                      <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="once">Once</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="annual">Annual</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Deadline offset (days from period end)</Label>
                      <Input type="number" value={form.default_deadline_offset_days} onChange={(e) => setForm({ ...form, default_deadline_offset_days: parseInt(e.target.value || "0", 10) })} />
                    </div>
                  </div>
                  <div>
                    <Label>Default reminder days-before (comma-separated)</Label>
                    <Input value={form.default_reminder_days_before.join(", ")} onChange={(e) => setForm({ ...form, default_reminder_days_before: e.target.value.split(",").map(x => parseInt(x.trim(), 10)).filter(Boolean) })} />
                  </div>
                  <div>
                    <Label>Stages (comma-separated)</Label>
                    <Input value={form.stages.join(", ")} onChange={(e) => setForm({ ...form, stages: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })} />
                  </div>
                  <DialogFooter><Button data-testid="save-template-btn" type="submit" className="bg-slate-900 hover:bg-slate-800">Create template</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )
        }
      />

      <div className="p-6 lg:p-8">
        <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.1em] text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Frequency</th>
                <th className="text-left px-4 py-2">Deadline offset</th>
                <th className="text-left px-4 py-2">Reminders (days)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500">No templates yet.</td></tr>}
              {rows.map((t) => (
                <tr key={t.template_id} data-testid="template-row" className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-slate-900">{t.name}</td>
                  <td className="px-4 py-3 text-slate-600 capitalize">{t.frequency}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-800">{t.default_deadline_offset_days}d</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-800">{(t.default_reminder_days_before || []).join(", ")}</td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin && (
                      <button onClick={() => remove(t.template_id)} className="text-slate-400 hover:text-rose-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
